/**
 * AttachmentPreviewDialog — in-app preview of an email attachment without
 * downloading it. Fetches a short-lived `inline`-disposition presigned URL and
 * renders images / PDFs in-page; other types fall back to a download prompt.
 */

import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Loader2, Download, FolderInput, ExternalLink } from 'lucide-react';
import { api } from '@/lib/api';
import { toast } from '@/components/ui/use-toast';
import { downloadFileToChosenLocation } from '../lib/download';
import { useEmailCalendarInvitePreview } from '../hooks/useMailbox';
import { CalendarInviteCard } from './CalendarInviteCard';
import { AddToCalendarButton } from './AddToCalendarButton';
import { isCalendarInvite } from '../lib/calendar-invite';

export interface PreviewAttachment {
  attachmentId: string;
  filename: string;
  mimeType: string;
  /** Underlying Vault document id; present ⇒ the attachment can be saved to a folder. */
  documentId?: string | null;
}

const baseMime = (m: string) => m.toLowerCase().split(';')[0].trim();
const isImage = (m: string) => m.startsWith('image/');
// Senders / IMAP parsers label PDFs inconsistently (application/pdf, x-pdf,
// acrobat, or a generic octet-stream with a .pdf name). Detect by either, so the
// preview affordance reliably appears and the iframe gets the PDF branch.
const PDF_MIME = /^application\/(pdf|x-pdf|acrobat|vnd\.pdf)$/i;
const hasPdfExt = (name?: string) => !!name && /\.pdf$/i.test(name.trim());
const isPdf = (m: string, name?: string) => PDF_MIME.test(m) || hasPdfExt(name);
// A meeting invite (.ics) (`isCalendarInvite`) is rendered as an event CARD,
// never a raw-text iframe — browsers have no inline renderer for text/calendar.
// text/calendar is excluded here (handled by the invite card above), leaving the
// iframe text branch for content browsers actually render inline.
const isText = (m: string) => {
  const b = baseMime(m);
  if (b === 'text/calendar') return false;
  return b.startsWith('text/') || b === 'application/json' || b === 'application/xml';
};

/** Whether the in-app preview can render this attachment (image / PDF / text / invite). */
export function isPreviewableAttachment(mimeType: string, filename?: string): boolean {
  return (
    isImage(mimeType) ||
    isPdf(mimeType, filename) ||
    isText(mimeType) ||
    isCalendarInvite(mimeType, filename)
  );
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  messageId: string | null;
  attachment: PreviewAttachment | null;
  /** Save a copy to the user's Documents; shown only when the attachment is saveable. */
  onSaveToVault?: () => void;
  /** Add a `.ics` invite to the calendar (no id = the invite calendar); shown
   *  only for calendar invites. */
  onAddToCalendar?: (calendarId?: string) => void;
  /** Disables the "Add to calendar" action while a request is in flight. */
  addingToCalendar?: boolean;
}

export function AttachmentPreviewDialog({
  open,
  onOpenChange,
  messageId,
  attachment,
  onSaveToVault,
  onAddToCalendar,
  addingToCalendar,
}: Props) {
  const [url, setUrl] = useState<string | null>(null);
  const [urlLoading, setUrlLoading] = useState(false);
  const [urlError, setUrlError] = useState<string | null>(null);

  const calendarInvite = attachment
    ? isCalendarInvite(attachment.mimeType, attachment.filename)
    : false;

  // Meeting invites (.ics) are parsed server-side into event fields and shown
  // as a card — the raw iCalendar text can't render inline. Shares its cache
  // with the reading pane's inline invite card. A missing object (e.g. bytes
  // purged) surfaces as a graceful error here, not a blank pane.
  const inviteQuery = useEmailCalendarInvitePreview(
    open && calendarInvite ? messageId : null,
    attachment?.attachmentId,
  );
  const invite = calendarInvite ? inviteQuery.data : undefined;
  const loading = calendarInvite ? inviteQuery.isLoading : urlLoading;
  const error = calendarInvite
    ? inviteQuery.error
      ? inviteQuery.error instanceof Error
        ? inviteQuery.error.message
        : 'Couldn’t load this meeting invite'
      : null
    : urlError;

  useEffect(() => {
    if (!open || !messageId || !attachment || calendarInvite) {
      setUrl(null);
      setUrlError(null);
      setUrlLoading(false);
      return;
    }
    let cancelled = false;
    setUrlLoading(true);
    setUrlError(null);
    setUrl(null);
    api
      .getEmailMailboxAttachmentDownloadUrl(messageId, attachment.attachmentId, 'inline')
      .then((res) => {
        if (!cancelled) setUrl(res.url);
      })
      .catch((e) => {
        if (!cancelled) setUrlError(e instanceof Error ? e.message : 'Failed to load preview');
      })
      .finally(() => {
        if (!cancelled) setUrlLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, messageId, attachment, calendarInvite]);

  const download = async () => {
    if (!messageId || !attachment) return;
    try {
      // Goes through the browser's own download UI (same-origin blob behind an
      // <a download>) — no OS save dialog, and never an <a> pointed at the
      // cross-origin S3 URL (that navigates the tab instead of downloading).
      await downloadFileToChosenLocation(attachment.filename, async () => {
        const { url: dl } = await api.getEmailMailboxAttachmentDownloadUrl(
          messageId,
          attachment.attachmentId,
        );
        const res = await fetch(dl);
        if (!res.ok) throw new Error(`Download failed (${res.status})`);
        return res.blob();
      });
    } catch (e) {
      toast({
        title: 'Couldn’t download attachment',
        description: e instanceof Error ? e.message : 'Please try again.',
        variant: 'destructive',
      });
    }
  };

  // Open the attachment in a new browser tab via its short-lived inline URL —
  // the reliable cross-browser way to "open" a PDF when the in-iframe viewer is
  // unavailable (Firefox, blocked plugin, etc.). Fetches a fresh URL so it works
  // even if the inline preview above failed to load.
  const openInNewTab = async () => {
    if (!messageId || !attachment) return;
    try {
      const { url: inlineUrl } = await api.getEmailMailboxAttachmentDownloadUrl(
        messageId,
        attachment.attachmentId,
        'inline',
      );
      window.open(inlineUrl, '_blank', 'noopener,noreferrer');
    } catch (e) {
      toast({
        title: 'Couldn’t open attachment',
        description: e instanceof Error ? e.message : 'Please try again.',
        variant: 'destructive',
      });
    }
  };

  const mime = attachment?.mimeType ?? '';
  const pdf = isPdf(mime, attachment?.filename);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-4xl">
        <DialogHeader>
          <DialogTitle className="truncate pr-6">
            {attachment?.filename ?? 'Attachment'}
          </DialogTitle>
        </DialogHeader>
        <div className="flex max-h-[70vh] min-h-[50vh] items-center justify-center overflow-auto">
          {loading ? (
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          ) : error ? (
            <p className="text-sm text-destructive">{error}</p>
          ) : invite ? (
            <CalendarInviteCard invite={invite} variant="dialog" />
          ) : url && isImage(mime) ? (
            <img
              src={url}
              alt={attachment?.filename ?? ''}
              className="max-h-[68vh] max-w-full object-contain"
            />
          ) : url && pdf ? (
            // Chromium's built-in PDF viewer is script-driven, so an EMPTY
            // sandbox renders a blank frame. Grant the two tokens the viewer
            // needs — `allow-scripts` (run the viewer) + `allow-same-origin`
            // (access its own S3 origin). This is safe here: the frame's origin
            // is the presigned S3 URL, NOT our app, so `allow-same-origin` does
            // not re-grant it access to us. Still no top-navigation, forms, or
            // popups. Falls back to the Download / Open-in-tab actions.
            <iframe
              src={url}
              title={attachment?.filename ?? 'PDF preview'}
              sandbox="allow-scripts allow-same-origin"
              className="h-[68vh] w-full"
            />
          ) : url && isText(mime) ? (
            // Sandboxed so a text/html attachment can't run scripts on preview.
            <iframe
              src={url}
              title={attachment?.filename ?? 'Text preview'}
              sandbox=""
              className="h-[68vh] w-full bg-white"
            />
          ) : (
            <div className="text-center text-sm text-muted-foreground">
              <p>Preview isn’t available for this file type.</p>
              <p className="text-xs">{mime || 'unknown type'}</p>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
          {/* "Open in new tab" uses an inline URL, which is blank for a .ics —
              hide it for invites (the card + Add-to-calendar cover that case). */}
          {!calendarInvite && (
            <Button variant="outline" className="gap-1" onClick={openInNewTab}>
              <ExternalLink className="h-4 w-4" /> Open in new tab
            </Button>
          )}
          {calendarInvite && onAddToCalendar && (
            <AddToCalendarButton
              size="default"
              onAdd={onAddToCalendar}
              disabled={addingToCalendar}
            />
          )}
          {onSaveToVault && attachment?.documentId && (
            <Button variant="outline" className="gap-1" onClick={onSaveToVault}>
              <FolderInput className="h-4 w-4" /> Save to Vault
            </Button>
          )}
          <Button className="gap-1" onClick={download}>
            <Download className="h-4 w-4" /> Download
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
